import { get, map, toNumber, kebabCase, reduce, isEmpty, isNil, split } from 'lodash';

import {
  GIFT_CART_RECIPIENT_EMAIL_ATTRIBUTE,
  GIFT_CART_RECIPIENT_NAME_ATTRIBUTE,
  GIFT_CART_SEND_ON_ATTRIBUTE,
} from 'constants/digitalGiftCard';
import Model from 'models';
import DigitalGiftCardUtils from 'utils/DigitalGiftCard';
import Log from 'utils/Log';
import StorefrontUtil from 'utils/Storefront';

import { UNISIZE_PRODS, GIFT_CARD_PRODS, DIGITAL_GIFT_CARD_PRODS } from './constants';

const StorefrontCartLineItemModel = class extends Model {
  get id() {
    const variantId = StorefrontUtil.convertGidtoId(this._data?.node?.merchandise?.id);
    let result = variantId;

    // Digital gift card can have multiple recipients
    // This ID is used as a key on a dictionary on App state where the Cart is persisted - [ID]: [StorefrontCartLineItemModel: {}]
    // To avoid override the same item ID on the Cart state when multiple DGC contains recipient email, then is appended the email hash to the ID
    if (this.isDigitalGiftCardWithRecipientEmail) {
      result = DigitalGiftCardUtils.generateDigitalGiftCardId({
        id: variantId,
        email: this.digitalGiftCardRecipientEmail,
      });
    }

    return result;
  }

  get lineItemType() {
    return get(this._data, 'lineItemType', 'VARIANT');
  }

  get storefrontProduct() {
    return this._data?.node?.merchandise?.product;
  }

  get quantity() {
    return this._data?.node?.quantity;
  }

  get title() {
    return this.storefrontProduct?.title;
  }

  get handle() {
    return this.storefrontProduct?.handle;
  }

  get description() {
    return this.storefrontProduct?.descriptionHtml;
  }

  get discountPerItem() {
    const discountAmount = this.discountAllocations?.amount || 0;
    const formattedDiscountAmount = Math.round((toNumber(discountAmount) * 100) / this.quantity);
    return formattedDiscountAmount;
  }

  get price() {
    const price = toNumber(this._data?.node?.merchandise?.price?.amount) * 100;
    const displayPrice = price - this.discountPerItem;

    return displayPrice;
  }

  get compareAtPrice() {
    let compareAtPrice = this._data.node?.merchandise?.compareAtPrice?.amount
      ? toNumber(this._data.node?.merchandise?.compareAtPrice?.amount) * 100
      : null;

    if (!isEmpty(this.discountAllocations) && isNil(compareAtPrice)) {
      compareAtPrice = toNumber(this._data?.node?.merchandise?.price?.amount) * 100;
    }

    return compareAtPrice;
  }

  get url() {
    return `/products/${this.storefrontProduct?.handle}`;
  }

  get fullName() {
    return this.storefrontProduct?.title;
  }

  get image() {
    return this.storefrontProduct?.featuredImage?.url;
  }

  get type() {
    return this.storefrontProduct?.productType;
  }

  get featuredImage() {
    return {
      src: this.storefrontProduct?.featuredImage?.url,
      altText: this.storefrontProduct?.featuredImage?.altText,
    };
  }

  get discountAllocations() {
    return this._data?.node?.discountAllocations?.[0]?.discountedAmount;
  }

  get product() {
    let contentfulImages = [];
    let cfMetadata = {};
    try {
      cfMetadata = JSON.parse(this.storefrontProduct?.cfMetadata?.value);
      contentfulImages = JSON.parse(this.storefrontProduct?.contentfulImages?.value);
    } catch (e) {
      Log.debug('error parsing CF metafields');
    }
    return {
      id: this.storefrontProduct?.id,
      handle: this.storefrontProduct?.handle,
      description: this.storefrontProduct?.descriptionHtml,
      compareAtPrice: this._data?.node?.merchandise?.compareAtPrice?.amount,
      fullName: this.storefrontProduct?.title,
      masterName: this.storefrontProduct?.masterName?.value,
      colorName: this.storefrontProduct?.colorName?.value,
      masterId: this.storefrontProduct?.masterId?.value,
      isUnisize: !!get(UNISIZE_PRODS, this.storefrontProduct?.masterId?.value),
      isGiftCard: !!get(GIFT_CARD_PRODS, this.storefrontProduct?.masterId?.value),
      isDigitalGiftCard: this.isDigitalGiftCard,
      type: this.storefrontProduct?.productType,
      featuredImage: {
        src: this.storefrontProduct?.featuredImage?.url,
        altText: this.storefrontProduct?.featuredImage?.altText,
      },
      contentfulImages: contentfulImages,
      gender: this.storefrontProduct?.gender?.value,
      hues: split(this.storefrontProduct?.hues?.value, ','),
      material: this.storefrontProduct?.material?.value,
      silhouette: this.storefrontProduct?.silhouette?.value,
      priceTier: this.storefrontProduct?.priceTier?.value,
      cfMetadata: cfMetadata,
      price: toNumber(this._data.node?.merchandise?.price?.amount) * 100,
    };
  }

  get variant() {
    return {
      id: this._data.node?.merchandise?.id,
      sku: this._data.node?.merchandise?.sku,
      value: this._data.node?.merchandise?.title,
      available: this._data.node?.merchandise?.availableForSale,
      backordered: this._data.node?.merchandise?.currentlyNotInStock,
      isFinalSale: this._data.node?.merchandise?.product?.tags?.includes('FINAL SALE'),
      price: toNumber(this._data.node?.merchandise?.price?.amount) * 100,
      compareAtPrice: this.compareAtPrice,
      handle: kebabCase(this._data.node?.merchandise?.title),
    };
  }

  get variant_options() {
    return map(this._data.node?.merchandise?.selectedOptions, ({ value }) => value);
  }

  get attributes() {
    return reduce(
      this._data?.node?.attributes,
      (acc, { key, value }) => {
        acc[key] = value;
        return acc;
      },
      {}
    );
  }

  get isDigitalGiftCard() {
    return !!get(DIGITAL_GIFT_CARD_PRODS, this.storefrontProduct?.masterId?.value);
  }

  get isDigitalGiftCardWithRecipientEmail() {
    return this.isDigitalGiftCard && !!this.digitalGiftCardRecipientEmail;
  }

  get digitalGiftCardRecipientEmail() {
    if (this.isDigitalGiftCard) {
      return get(this.attributes, GIFT_CART_RECIPIENT_EMAIL_ATTRIBUTE);
    } else {
      return null;
    }
  }

  get digitalGiftCardRecipientName() {
    if (this.isDigitalGiftCard) {
      return get(this.attributes, GIFT_CART_RECIPIENT_NAME_ATTRIBUTE);
    } else {
      return null;
    }
  }

  get digitalGiftCardRecipientSendOn() {
    if (this.isDigitalGiftCard) {
      return get(this.attributes, GIFT_CART_SEND_ON_ATTRIBUTE);
    } else {
      return null;
    }
  }
};

export default StorefrontCartLineItemModel;
